import React from 'react';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

interface NotificationProps {
  type: NotificationType;
  message?: string;
  onClose?: () => void;
  autoClose?: boolean;
  duration?: number;
  closeOnClick?: boolean;
  children?: React.ReactNode;
  icon?: React.ReactNode;
}

const Notification: React.FC<NotificationProps> = ({ 
  type, 
  message, 
  onClose, 
  autoClose = true, 
  duration = 5000,
  closeOnClick = false,
  children,
  icon,
}) => {
  React.useEffect(() => {
    if (autoClose && onClose) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [autoClose, onClose, duration]);

  const getIcon = () => {
    switch (type) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'info': return 'ℹ️';
      default: return 'ℹ️';
    }
  };

  const getStyles = () => {
    const baseStyles = {
      display: 'flex',
      alignItems: 'center',
      padding: '1rem 1.5rem',
      borderRadius: '12px',
      marginBottom: '1rem',
      fontSize: '0.95rem',
      fontWeight: '500',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      border: '1px solid',
      animation: 'slideIn 0.3s ease-out',
    };

    switch (type) {
      case 'success':
        return {
          ...baseStyles,
          backgroundColor: '#f0fdf4',
          borderColor: '#bbf7d0',
          color: '#166534',
        };
      case 'error':
        return {
          ...baseStyles,
          backgroundColor: '#fef2f2',
          borderColor: '#fecaca',
          color: '#dc2626',
        };
      case 'warning':
        return {
          ...baseStyles,
          backgroundColor: '#fffbeb',
          borderColor: '#fed7aa',
          color: '#d97706',
        };
      case 'info':
        return {
          ...baseStyles,
          backgroundColor: '#eff6ff',
          borderColor: '#bfdbfe',
          color: '#1d4ed8',
        };
      default:
        return baseStyles;
    }
  };

  const containerProps = {
    style: getStyles() as React.CSSProperties,
    onClick: closeOnClick && onClose ? onClose : undefined,
  };

  return (
    <div {...containerProps}>
      <span style={{ marginRight: '0.75rem', fontSize: '1.2rem' }}>
        {icon ?? getIcon()}
      </span>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {children ? children : <span>{message}</span>}
      </div>
      {onClose && (
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '1.4rem',
            cursor: 'pointer',
            width: 36,
            height: 36,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: '0.5rem',
            borderRadius: 8,
            opacity: 0.85,
            color: 'inherit',
            lineHeight: 1,
          }}
          aria-label="Close notification"
        >
          ×
        </button>
      )}
    </div>
  );
};

export default Notification; 